import { openModal } from 'actions/modal';
import {
  getAllArchivedHubs,
  getAllArchivedKits,
  getAllArchivedTemplates,
  getAllHubs,
  getAllKits,
  getAllTemplates,
  getFeaturedDiscoverItems,
} from 'actions/requests';
import Button from 'components/atoms/Button';
import AdminAuthoringFeaturedSection from 'pages/_AdminAuthoringPages/AdminAuthoringHome/AdminAuthoringFeaturedSection';
import NullState from 'components/organisms/NullState';
import { SkeletonDiscover } from 'components/organisms/SkeletonWrap';
import { DiscoverItemCard, FeatureableDiscoverItem, Hubs, Kits, Templates } from 'utils/gloo-types';
import DiscoverSectionHeaderPreview from 'pages/_AdminAuthoringPages/AdminAuthoringHome/DiscoverSectionHeaderPreview';
import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import {
  discoverItemCardForHub,
  discoverItemCardForKit,
  discoverItemCardForTemplate,
  featureableDiscoverItemForHub,
  featureableDiscoverItemForKit,
  reduceSortDiscoverItemsByAuthorAndTitle,
  sortFeatureableDiscoverItemsByTitle,
  useDiscoverItemAuthorSelect,
} from 'utils/discover';
import { useQuery } from 'utils/hooks';
import { useThunkDispatch } from 'utils/reduxHook';
import InAppRoutes, { pushProgressive } from 'utils/router';
import LinkOrPorted from 'components/atoms/LinkOrPorted';
import { twMerge } from 'tailwind-merge';
import AllAuthorItems from './AllAuthorItems';

const AdminAuthoringHome = () => {
  const dispatch = useThunkDispatch();

  const query = useQuery();
  const authorParam = query.get('author');
  const archiveParam = query.get('archive');

  const location = useLocation();
  const history = useHistory();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [hubs, setHubs] = useState<Hubs[]>([]);
  const [kits, setKits] = useState<Kits[]>([]);
  const [templates, setTemplates] = useState<Templates[]>([]);

  const [archivedHubs, setArchivedHubs] = useState<Hubs[]>([]);
  const [archivedKits, setArchivedKits] = useState<Kits[]>([]);
  const [archivedTemplates, setArchivedTemplates] = useState<Templates[]>([]);

  const [featuredItems, setFeaturedItems] = useState<FeatureableDiscoverItem[]>([]);

  useEffect(() => {
    setIsLoading(true);
    Promise.all([
      dispatch(getAllHubs()).then(setHubs),
      dispatch(getAllKits()).then(setKits),
      dispatch(getAllTemplates()).then(setTemplates),
      dispatch(getAllArchivedHubs()).then(setArchivedHubs),
      dispatch(getAllArchivedKits()).then(setArchivedKits),
      dispatch(getAllArchivedTemplates()).then(setArchivedTemplates),
      dispatch(getFeaturedDiscoverItems()).then(setFeaturedItems),
    ]).finally(() => {
      setIsLoading(false);
    });
  }, []);

  const handleCardClick = useDiscoverItemAuthorSelect();

  // this is the data for all unarchived content
  const splitDiscoverItemsIntoAuthorArrays = (): DiscoverItemCard[][] => {
    const discoverItems = [
      ...hubs.map(discoverItemCardForHub),
      ...kits.map(discoverItemCardForKit),
      ...templates.map(discoverItemCardForTemplate),
    ];
    return reduceSortDiscoverItemsByAuthorAndTitle(discoverItems);
  };

  const sortedDiscoverItemsIntoAuthors = useMemo(() => splitDiscoverItemsIntoAuthorArrays(), [hubs, kits, templates]);

  const authorItems = useMemo(
    () =>
      !authorParam
        ? []
        : sortedDiscoverItemsIntoAuthors.filter((item) => (item[0].metadata.author || 'Gloo') === authorParam)[0],
    [authorParam, hubs, kits, templates],
  );

  const authorItemComponents = useMemo(
    () =>
      sortedDiscoverItemsIntoAuthors.map((items) => {
        // since all arrays are grouped by the same author we can safely assume the first index will match the rest
        const author = items[0].metadata.author || 'Gloo';
        return (
          <DiscoverSectionHeaderPreview
            wrapperStyleClassName="mb-4 mt-8"
            key={author}
            title={author}
            itemCards={items.slice(0, 12)}
            onViewAll={() => {
              history.push({ pathname: location.pathname, search: `?author=${encodeURIComponent(author)}` });
            }}
            handleCardClick={handleCardClick}
          />
        );
      }),
    [hubs, kits, templates],
  );

  // this is the data for all archived content
  const splitArchivedDiscoverItemsIntoAuthorArrays = (): DiscoverItemCard[][] => {
    const discoverItems = [
      ...archivedHubs.map(discoverItemCardForHub),
      ...archivedKits.map(discoverItemCardForKit),
      ...archivedTemplates.map(discoverItemCardForTemplate),
    ];
    return reduceSortDiscoverItemsByAuthorAndTitle(discoverItems);
  };
  const sortedArchivedDiscoverItemsIntoAuthors = useMemo(
    () => splitArchivedDiscoverItemsIntoAuthorArrays(),
    [archivedHubs, archivedKits, archivedTemplates],
  );

  const archivedAuthorItems = useMemo(
    () =>
      !authorParam
        ? []
        : sortedArchivedDiscoverItemsIntoAuthors.filter(
            (item) => (item[0].metadata.author || 'Gloo') === authorParam,
          )[0],
    [authorParam, archivedHubs, archivedKits, archivedTemplates],
  );

  const archivedAuthorItemComponents = useMemo(
    () =>
      sortedArchivedDiscoverItemsIntoAuthors.map((items) => {
        // since all arrays are grouped by the same author we can safely assume the first index will match the rest
        const author = items[0].metadata.author || 'Gloo';
        return (
          <DiscoverSectionHeaderPreview
            wrapperStyleClassName="mb-4 mt-4"
            key={author}
            title={author}
            itemCards={items.slice(0, 12)}
            onViewAll={() => {
              history.push({
                pathname: location.pathname,
                search: `?archive=TRUE&author=${encodeURIComponent(author)}`,
              });
            }}
            handleCardClick={handleCardClick}
          />
        );
      }),
    [archivedHubs, archivedKits, archivedTemplates],
  );

  const featureableItems = useMemo(
    () =>
      [...hubs.map(featureableDiscoverItemForHub), ...kits.map(featureableDiscoverItemForKit)].sort(
        sortFeatureableDiscoverItemsByTitle,
      ),
    [hubs, kits],
  );

  return isLoading ? (
    <SkeletonDiscover />
  ) : (
    <div>
      <header className="flex justify-between mb-4">
        <div className="flex flex-row gap-12">
          <LinkOrPorted
            type="navlink"
            to={InAppRoutes.AdminAuthoringHome}
            className={twMerge(
              'inline-block nav-link hover:text-stormBlue-300 text-charcoal',
              !archiveParam &&
                'hover:text-stormBlue-300 text-charcoal font-medium active border-b-4 border-primary-500',
            )}
          >
            Library
          </LinkOrPorted>
          <LinkOrPorted
            type="navlink"
            to={InAppRoutes.AdminAuthoringArchiveHome}
            className={twMerge(
              'inline-block nav-link hover:text-stormBlue-300 text-charcoal',
              !!archiveParam &&
                'hover:text-stormBlue-300 text-charcoal font-medium active border-b-4 border-primary-500',
            )}
          >
            Archive
          </LinkOrPorted>
        </div>
        <div>
          <Button
            onClick={() => pushProgressive(InAppRoutes.AdminHubCreate)}
            className="mx-2"
            icon="edit"
            dataDurableId="adminHubsAddHubButton"
            type="tertiary"
            text="Add Hub"
          />

          <Button
            onClick={() => pushProgressive(InAppRoutes.AdminKitCreate)}
            className="mx-2"
            icon="edit"
            dataDurableId="adminKitsAddKitButton"
            type="primary"
            text="Add Kit"
          />
          <Button
            onClick={() => dispatch(openModal('ThryveAdminNewTemplateType'))}
            className="mx-2"
            icon="edit"
            dataDurableId="adminKitsAddTemplateButton"
            type="secondary"
            text="Add Template"
          />
        </div>
      </header>
      {!authorParam ? (
        <div>
          {!archiveParam ? (
            <>
              <AdminAuthoringFeaturedSection
                featuredItems={featuredItems}
                setFeaturedItems={setFeaturedItems}
                featureableItems={featureableItems}
              />
              {authorItemComponents}
            </>
          ) : !archivedAuthorItemComponents?.length ? (
            <NullState type="discoverSwimlane" />
          ) : (
            archivedAuthorItemComponents
          )}
        </div>
      ) : (
        <AllAuthorItems
          authorDiscoverItems={!archiveParam ? authorItems : archivedAuthorItems}
          archived={!!archiveParam}
        />
      )}
    </div>
  );
};

export default AdminAuthoringHome;
